import { IS_OFFLINE, readThrough, request } from "./http";
import { securityAlerts, securityScanFindings, securitySummary } from "@/mocks/securityAlerts";

/**
 * Security console data layer.
 *
 * All actions hit the single admin endpoint `/api/admin/security` (GET for the
 * overview, POST with an `action` for scans/resolves) which matches the Apache
 * clean-path rewrite. Reads fall back to demo data while the project runs
 * without a live API, and actions degrade gracefully offline.
 */

export interface SecurityAlert {
  id: string;
  severity: string;
  type: string;
  title: string;
  detail: string;
  source: string;
  subject: string;
  ip: string;
  status: string;
  detectedAt: string;
  resolvedAt: string | null;
}

export interface SecurityScanFinding {
  id: string;
  severity: string;
  type: string;
  title: string;
  detail: string;
  subject: string;
  matchedPattern: string;
}

export interface SecurityScanResult {
  scanned: number;
  flagged: number;
  finishedAt: string;
  findings: SecurityScanFinding[];
}

export interface SecuritySummary {
  openAlerts: number;
  criticalOpen: number;
  resolvedToday: number;
  integrityOk: boolean;
  lastScanAt: string;
  integrityBaselineAt: string;
  watchedFiles: number;
}

export interface SecurityIntegrity {
  status: string;
  added: string[];
  modified: string[];
  removed: string[];
}

export interface SecurityOverview {
  summary: SecuritySummary;
  alerts: SecurityAlert[];
  integrity: SecurityIntegrity;
}

const DEMO_OVERVIEW: SecurityOverview = {
  summary: securitySummary as SecuritySummary,
  alerts: securityAlerts as SecurityAlert[],
  integrity: { status: "clean", added: [], modified: [], removed: [] },
};

export function getSecurityOverview(token: string | null): Promise<SecurityOverview> {
  return readThrough<SecurityOverview>("/admin/security", DEMO_OVERVIEW, { token });
}

export async function runSecurityScan(token: string | null): Promise<SecurityScanResult> {
  if (IS_OFFLINE) {
    return {
      scanned: 42,
      flagged: securityScanFindings.length,
      finishedAt: new Date().toISOString(),
      findings: securityScanFindings as SecurityScanFinding[],
    };
  }
  return request<SecurityScanResult>("/admin/security", { method: "POST", token, body: { action: "scan" } });
}

export async function resolveSecurityAlert(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) return true;
  try {
    await request("/admin/security", { method: "POST", token, body: { action: "resolve", id } });
    return true;
  } catch {
    return false;
  }
}